/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Grid, PageGridContainer, Typography2, breakpoints, useMediaQuery } from '@coursera/cds-core';
import { useVisibilityTracker } from '@coursera/event-pulse/react';

import TrackedDiv from 'bundles/page/components/TrackedDiv';
import LazyImgix from 'bundles/page/components/shared/LazyImgix';
import Section from 'bundles/unified-description-page-common/components/shared/Section';
import { useEventingProductData } from 'bundles/unified-description-page-common/contexts/EventingContext';

// A custom breakpoint to better handle layout between larger and smaller devices.
const BREAKPOINT = 768 as const;

interface Props {
  title: string;
  anchorId?: string;
  valueProps?: string[];
  subtitle?: React.ReactNode;
  disclaimers?: string | null;
  imageSrc?: string | null;
}

const styles = {
  moduleContainer: css`
    position: relative;
    width: 100%;
    border: 2px solid var(--cds-color-grey-200);
    border-radius: var(--cds-border-radius-200);
    padding: var(--cds-spacing-200);
    overflow: hidden;
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: space-between;
    gap: var(--cds-spacing-200);

    > * {
      max-width: 50%;

      &:first-child {
        align-self: center;
      }
    }

    ${breakpoints.down(BREAKPOINT)} {
      border: 1px solid var(--cds-color-grey-200);
      border-radius: calc(var(--cds-border-radius-200) + var(--cds-border-radius-50));
      padding: var(--cds-spacing-200);
      flex-direction: column;
      justify-content: flex-start;
      align-items: flex-start;

      > * {
        max-width: 100%;
        width: 100%;
      }
    }
  `,
  contentContainer: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-100);
    max-width: 100%;
    padding-left: var(--cds-spacing-100);

    ${breakpoints.down(BREAKPOINT)} {
      gap: var(--cds-spacing-150);
      padding-left: 0;
    }
  `,
  valueProps: css`
    list-style: disc;
    padding-left: var(--cds-spacing-300);

    ${breakpoints.down(BREAKPOINT)} {
      padding-left: var(--cds-spacing-200);
    }

    li {
      margin-bottom: var(--cds-spacing-50);
    }
  `,
  imageContainer: css`
    border-radius: var(--cds-border-radius-100);
    overflow: hidden;

    > div {
      width: 100%;
      height: auto;
      padding-bottom: unset;
      max-width: none;
    }

    img {
      width: 100%;
    }
  `,
};

const CareerOutcomesModule = ({ title, subtitle, anchorId, valueProps, disclaimers, imageSrc }: Props) => {
  const eventingProductData = useEventingProductData();
  const isMobile = useMediaQuery(breakpoints.down(BREAKPOINT));

  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_page_section',
    {
      pageSection: {
        sectionName: 'outcomes',
      },
      ...eventingProductData,
    }
  );

  return (
    <TrackedDiv
      trackingName="career_outcomes"
      trackClicks={false}
      requireFullyVisible={false}
      withVisibilityTracking={true}
    >
      <Section anchorId={anchorId} ref={visibilityTrackingRef}>
        <PageGridContainer>
          <Grid item xs={12}>
            <div css={styles.moduleContainer}>
              <div css={styles.contentContainer}>
                <Typography2 variant="subtitleLarge" component="h2">
                  {title}
                </Typography2>
                {subtitle}
                {isMobile && imageSrc && (
                  <div css={styles.imageContainer}>
                    <LazyImgix maxWidth={736} maxHeight={289} src={imageSrc} alt="" imgParams={{ fit: 'crop' }} />
                  </div>
                )}
                {valueProps && (
                  <Typography2 variant="bodySecondary" component="div">
                    <ul css={styles.valueProps}>
                      {valueProps.map((valueProp) => {
                        return <li key={valueProp}>{valueProp}</li>;
                      })}
                    </ul>
                  </Typography2>
                )}
                {disclaimers && (
                  <Typography2 component="p" variant="bodySecondary" color="supportText">
                    {disclaimers}
                  </Typography2>
                )}
              </div>
              {!isMobile && imageSrc && (
                <div css={styles.imageContainer}>
                  <LazyImgix maxWidth={511} maxHeight={202} src={imageSrc} alt="" imgParams={{ fit: 'crop' }} />
                </div>
              )}
            </div>
          </Grid>
        </PageGridContainer>
      </Section>
    </TrackedDiv>
  );
};

export default CareerOutcomesModule;
